// Henge Inc.
import HUI from "@henge-inc/ui/v1_2";
// css
import "@/pages/_app.css";
import "@/components/vault/viewer/stickers/tiptap.scss";
import DefaultLayout from "@/components/DefaultLayout";
import { PopupError } from "@/components/popup/error/PopupError";
import {
  useGlobalActions,
  usePopupError,
} from "@/stores/zustand/useGlobalStore";
import { NextPage } from "next";
import type { AppProps } from "next/app";
import React, { useEffect } from "react";
import { MOBILE_SCREEN_WIDTH, useScreenStore } from "@/stores/useScreenStore";
import { OverlayScrollbars } from "overlayscrollbars";
import { useDarkMode } from "usehooks-ts";
import { PortalRoot } from "@/components/portal/PortalRoot";
import { Theme as RadixTheme } from "@radix-ui/themes";
import DefaultHead from "@/components/DefaultHead";
import dynamic from "next/dynamic";
// Vercel Analytics
const Analytics = dynamic(
  () => import("@vercel/analytics/next").then((m) => m.Analytics),
  { ssr: false },
);

interface CustomAppProps extends AppProps {
  Component: NextPage & {
    Layout?: ({
      children,
    }: {
      children?: React.ReactNode;
    }) => React.ReactElement;
  };
}

export default function MyApp({ Component, pageProps }: CustomAppProps) {
  const { isShowingPopupError, popupErrorMessageList } = usePopupError();
  const { initRootScrollbar, clearRootScrollbar, resetPopupError } =
    useGlobalActions();

  const Layout: CustomAppProps["Component"]["Layout"] =
    Component.Layout || DefaultLayout;

  const { setIsMobileScreen } = useScreenStore();

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width || window.innerWidth;
      setIsMobileScreen(width < MOBILE_SCREEN_WIDTH);
    });

    resizeObserver.observe(document.documentElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setIsMobileScreen]);

  const { isDarkMode } = useDarkMode();
  useEffect(() => {
    const htmlEl = document.documentElement;
    htmlEl.classList.toggle("dark", isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const target = document.body;
    if (!target) return;

    clearRootScrollbar();
    initRootScrollbar(
      OverlayScrollbars(target, {
        overflow: {
          x: "hidden",
          y: "scroll",
        },
        scrollbars: {
          theme: isDarkMode ? "os-theme-light" : "os-theme-dark",
          autoHide: "move",
        },
      }),
    );

    return () => {
      clearRootScrollbar();
    };
  }, [isDarkMode]);

  return (
    <>
      <Analytics />

      <RadixTheme hasBackground={false}>
        <DefaultHead />
        <HUI.GlobalStyle />

        <PopupError
          handle={isShowingPopupError}
          resetHandler={resetPopupError}
          messageList={popupErrorMessageList}
        />
        {/*<MessageModal />*/}

        <Layout {...pageProps}>
          <Component {...pageProps} />
        </Layout>

        {portalList.map((portal) => (
          <PortalRoot
            key={portal.id}
            id={portal.id}
            className={portal.className}
            style={portal.style}
            zIndex={portal.zIndex}
          />
        ))}
      </RadixTheme>
    </>
  );
}

const portalList: {
  id: string;
  className?: string;
  style?: React.CSSProperties;
  zIndex?: number;
}[] = [
  {
    id: "vault-object-modal-root",
    className: "modal-root",
    zIndex: 3300,
  },
  {
    id: "vault--object--upload--portal--root",
    className: "grid__wrapper",
    zIndex: 3350,
  },
  {
    id: "modal--portal--root",
    className: "grid__wrapper",
    zIndex: 3400,
  },
  {
    id: "popup--portal--root",
    className: "grid__wrapper",
    zIndex: 4000,
  },
];
